import { useState } from 'react';
import { motion } from 'framer-motion';
import { PlayAreaMobile } from './Sidebar';

export default function AboutSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const skills = [
    { category: 'Design', items: ['UI/UX', 'Creative Design', 'Branding', 'Graphic Design'] },
    { category: 'Strategy', items: ['Marketing', 'UX Strategy', 'Data Visualization'] },
    { category: 'Tools', items: ['Figma', 'Adobe Suite', 'Prototyping'] },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('subject', formData.subject);
      body.append('message', formData.message);

      const res = await fetch('https://readdy.ai/api/form/d66d6osn78sauj0embog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <motion.section
        id="about"
        className="mt-12"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        {/* Section Header */}
        <div className="flex items-center gap-2 mb-6">
          <span className="w-5 h-5 flex items-center justify-center text-notion-secondary">
            <i className="ri-user-line text-base"></i>
          </span>
          <h2 className="font-serif text-lg font-medium text-notion-text">About Me</h2>
        </div>

        <div className="border border-notion-border rounded-lg bg-notion-sidebar p-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Text Content */}
            <div className="flex-1">
              <h3 className="font-serif text-xl font-medium text-notion-text mb-4">
                Hi! I&apos;m Annie <span className="inline-block ml-1">&#x1F44B;</span>
              </h3>

              <div className="space-y-3 text-[13px] leading-[1.7] text-notion-secondary">
                <p>
                  I&apos;m a product-minded designer and operator building at the intersection of business, gaming, anime, comics, and technology. I&apos;m drawn to ambitious ideas that live online and in fandom spaces, and I love the 0 → 1 grind of turning something from concept to reality.
                </p>
                <p>
                  I previously served as President of the Economics and Business Student Association (EBSA) at UC Davis, leading a 35-person team and supporting 100+ members through career initiatives and partnerships.
                </p>
                <p>
                  I also co-founded Voithos, a student organization focused on addressing food insecurity and supporting local businesses.
                </p>
                <p>
                  Beyond campus, I design end-to-end product experiences in startup environments, working closely with engineers and shaping growth strategy. I care about building systems that are thoughtful, scalable, and human.
                </p>
                <p>
                  At my core, I&apos;m someone who likes building worlds, whether that&apos;s through digital products or the games and stories that inspire me. If I&apos;m not working, I&apos;m probably exploring a new title, reading manga, or analyzing why certain experiences resonate so deeply.
                </p>
                <p>
                  Always open to bold ideas and meaningful conversations.{' '}
                  <a
                    href="mailto:annieding000@gmail.com"
                    className="text-notion-text underline decoration-notion-border hover:decoration-notion-text transition-colors cursor-pointer"
                  >
                    Let&apos;s build something interesting
                  </a>
                  !
                </p>
                <p className="pt-2">
                  Connect with me on{' '}
                  <a
                    href="https://www.instagram.com/ani_fatal/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-notion-text underline decoration-notion-border hover:decoration-notion-text transition-colors cursor-pointer"
                  >
                    Instagram
                  </a>
                  ,{' '}
                  <a
                    href="https://www.linkedin.com/in/annieding04/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-notion-text underline decoration-notion-border hover:decoration-notion-text transition-colors cursor-pointer"
                  >
                    LinkedIn
                  </a>
                  , or{' '}
                  <a
                    href="mailto:annieding000@gmail.com"
                    className="text-notion-text underline decoration-notion-border hover:decoration-notion-text transition-colors cursor-pointer"
                  >
                    email
                  </a>
                  .
                </p>
              </div>
            </div>

            {/* Skills Sidebar */}
            <div className="lg:w-[200px] lg:min-w-[200px]">
              <div className="space-y-5">
                {skills.map((group) => (
                  <div key={group.category}>
                    <h4 className="text-[11px] font-medium text-notion-secondary uppercase tracking-widest mb-2">
                      {group.category}
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {group.items.map((item) => (
                        <span
                          key={item}
                          className="text-[11px] text-notion-secondary bg-notion-tag px-2 py-1 rounded"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Play Area - Mobile Only */}
      <motion.div
        className="mt-12 lg:hidden"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.5 }}
      >
        <PlayAreaMobile />
      </motion.div>

      {/* Contact Me Section */}
      <motion.section
        id="contact"
        className="mt-12"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        {/* Section Header */}
        <div className="flex items-center gap-2 mb-6">
          <span className="w-5 h-5 flex items-center justify-center text-notion-secondary">
            <i className="ri-pencil-line text-base"></i>
          </span>
          <h2 className="font-serif text-lg font-medium text-notion-text">Contact Me</h2>
        </div>

        <div className="border border-notion-border rounded-lg bg-notion-sidebar p-6">
          <p className="text-[13px] text-notion-secondary leading-relaxed mb-6">
            Have a project in mind, want to collaborate, or just say hi? Drop me a message and I&apos;ll get back
            to you soon.
          </p>

          <form
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="contact-name"
                  className="block text-[12px] font-medium text-notion-text mb-1.5"
                >
                  Name <span className="text-notion-accent">*</span>
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Your name"
                  className="w-full text-sm px-3 py-2 rounded-md border border-notion-border bg-white text-notion-text placeholder:text-notion-secondary/50 focus:outline-none focus:border-notion-accent transition-colors"
                />
              </div>
              <div>
                <label
                  htmlFor="contact-email"
                  className="block text-[12px] font-medium text-notion-text mb-1.5"
                >
                  Email <span className="text-notion-accent">*</span>
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="your@email.com"
                  className="w-full text-sm px-3 py-2 rounded-md border border-notion-border bg-white text-notion-text placeholder:text-notion-secondary/50 focus:outline-none focus:border-notion-accent transition-colors"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="contact-subject"
                className="block text-[12px] font-medium text-notion-text mb-1.5"
              >
                Subject
              </label>
              <input
                id="contact-subject"
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="What is this about?"
                className="w-full text-sm px-3 py-2 rounded-md border border-notion-border bg-white text-notion-text placeholder:text-notion-secondary/50 focus:outline-none focus:border-notion-accent transition-colors"
              />
            </div>

            <div>
              <label
                htmlFor="contact-message"
                className="block text-[12px] font-medium text-notion-text mb-1.5"
              >
                Message <span className="text-notion-accent">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                maxLength={500}
                rows={5}
                placeholder="Tell me about your project or idea..."
                className="w-full text-sm px-3 py-2 rounded-md border border-notion-border bg-white text-notion-text placeholder:text-notion-secondary/50 focus:outline-none focus:border-notion-accent transition-colors resize-none"
              />
              <div className="text-right text-[11px] text-notion-secondary mt-1">
                {formData.message.length}/500
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="text-[13px] font-medium px-5 py-2 rounded-md bg-notion-text text-white hover:bg-notion-text/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <span className="text-[12px] text-emerald-600 flex items-center gap-1">
                  <i className="ri-check-line text-sm"></i>
                  Message sent successfully!
                </span>
              )}
              {submitStatus === 'error' && (
                <span className="text-[12px] text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line text-sm"></i>
                  Something went wrong. Please try again.
                </span>
              )}
            </div>
          </form>
        </div>
      </motion.section>
    </>
  );
}
